import { useCallback, useEffect, useRef, useState } from "react";
import { listKbSectionsPaginated, listKbTags } from "../services/gameRuleKbSectionService";
import type { KbSectionListRow, KbTagOption } from "../types/gameRuleKbSection";

const SEARCH_DEBOUNCE_MS = 400;

export type UseGameRuleKbSectionsResult = {
  readonly rows: readonly KbSectionListRow[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly page: number;
  readonly pageSize: number;
  readonly totalRows: number;
  readonly searchInput: string;
  readonly tagFilter: string;
  readonly tagOptions: readonly KbTagOption[];
  readonly tagsLoading: boolean;
  readonly setSearchInput: (value: string) => void;
  readonly setTagFilter: (value: string) => void;
  readonly setPage: (page: number) => void;
  readonly setPageSize: (pageSize: number) => void;
  readonly reload: () => void;
};

export function useGameRuleKbSections(): UseGameRuleKbSectionsResult {
  const [rows, setRows] = useState<KbSectionListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPageState] = useState(1);
  const [pageSize, setPageSizeState] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [searchInput, setSearchInputState] = useState("");
  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilterState] = useState("");
  const [tagOptions, setTagOptions] = useState<KbTagOption[]>([]);
  const [tagsLoading, setTagsLoading] = useState(true);
  const [reloadToken, setReloadToken] = useState(0);
  const searchTimerRef = useRef<number | null>(null);

  const setSearchInput = useCallback((value: string) => {
    setSearchInputState(value);
    setPageState(1);
  }, []);

  const setTagFilter = useCallback((value: string) => {
    setTagFilterState(value);
    setPageState(1);
  }, []);

  const setPage = useCallback((next: number) => {
    setPageState(next);
  }, []);

  const setPageSize = useCallback((next: number) => {
    setPageSizeState(next);
    setPageState(1);
  }, []);

  const reload = useCallback(() => {
    setReloadToken((v) => v + 1);
  }, []);

  useEffect(() => {
    if (searchTimerRef.current) {
      window.clearTimeout(searchTimerRef.current);
    }
    searchTimerRef.current = window.setTimeout(() => {
      setSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchTimerRef.current) {
        window.clearTimeout(searchTimerRef.current);
      }
    };
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    setTagsLoading(true);
    void listKbTags()
      .then((tags) => {
        if (!cancelled) setTagOptions([...tags]);
      })
      .catch(() => {
        if (!cancelled) setTagOptions([]);
      })
      .finally(() => {
        if (!cancelled) setTagsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const { items, total } = await listKbSectionsPaginated({
          page,
          limit: pageSize,
          search: search || undefined,
          tag: tagFilter || undefined,
          status: "active",
        });
        if (!cancelled) {
          setRows(items);
          setTotalRows(total);
        }
      } catch (e) {
        if (!cancelled) {
          setRows([]);
          setTotalRows(0);
          setError(e instanceof Error ? e.message : "Unable to load KB sections.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [page, pageSize, search, tagFilter, reloadToken]);

  return {
    rows,
    loading,
    error,
    page,
    pageSize,
    totalRows,
    searchInput,
    tagFilter,
    tagOptions,
    tagsLoading,
    setSearchInput,
    setTagFilter,
    setPage,
    setPageSize,
    reload,
  };
}
